import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { CLEANING_MAINTENANCE_PER_UNIT, RESERVATION_FEE_PERCENT } from 'src/shared/components/subscription-plan-card/subscription-plan-card.component';

export type SubscriptionChangeMode = 'add' | 'remove' | 'subscribe';

interface SubscriptionLineItem {
  icon: string;
  title: string;
  subtitle?: string;
  amount: string;
  struck?: boolean;
}

interface SubscriptionModalCopy {
  icon: string;
  title: string;
  body: string;
  confirmText: string;
  dismissText?: string;
  totalLabel: string;
}

/**
 * Confirmation prompt for the Cleaning & Maintenance add-on — both the paywall
 * SubscriptionGateService shows when an un-upgraded client hits a gated feature, and the
 * subscription page's add-on toggle:
 *
 *   'add'       — buy the add-on (the original upsell)
 *   'remove'    — drop it, keeping Full Autopilot property management
 *   'subscribe' — no plan yet: the confirm opens the Subscribe page, where the card and the
 *                 first payment are taken
 *
 * 'remove' is the "are you sure?" step: it leads with the consequence, strikes through what
 * is going away, and pairs the confirm button with a "keep it" escape. Purely presentational
 * — confirming dismisses with { confirm: true } and the service performs the plan change.
 */
@Component({
  selector: 'subscription-upgrade-modal',
  templateUrl: './subscription-upgrade-modal.component.html',
  styleUrls: ['./subscription-upgrade-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionUpgradeModalComponent implements OnInit {

  private modalController = inject(ModalController);

  // All assigned via ModalController componentProps.
  @Input() mode: SubscriptionChangeMode = 'add';
  @Input() unitCount = 1;
  @Input() perUnitDollars = CLEANING_MAINTENANCE_PER_UNIT;
  @Input() feePercent = RESERVATION_FEE_PERCENT;

  // Built once from the inputs so the template never calls back into the component.
  copy: SubscriptionModalCopy;
  lineItems: SubscriptionLineItem[];
  total: string;
  isDestructive: boolean;

  ngOnInit() {
    this.isDestructive = this.mode === 'remove';
    this.copy = this.buildCopy();
    this.lineItems = this.buildLineItems();
    this.total = this.buildTotal();
  }

  confirm = () => {
    this.modalController.dismiss({ confirm: true });
  };

  dismiss = () => {
    this.modalController.dismiss();
  };

  private get cleaningMaintenanceTotal(): string {
    return this.money(this.unitCount * this.perUnitDollars);
  }

  private get bookingFee(): string {
    return `${this.feePercent}% of bookings`;
  }

  private buildCopy(): SubscriptionModalCopy {
    if (this.mode === 'remove') {
      return {
        icon: 'remove_circle_outline',
        title: 'Remove Cleaning & Maintenance?',
        body: 'Are you sure? TIDY will stop coordinating your cleanings, turnovers, maintenance, inspections & restocking. You keep Full Autopilot property management and can add Cleaning & Maintenance back any time.',
        confirmText: 'Yes, Remove Cleaning & Maintenance',
        dismissText: 'Keep Cleaning & Maintenance',
        totalLabel: 'New Monthly Total'
      };
    }
    if (this.mode === 'subscribe') {
      return {
        icon: 'auto_awesome',
        title: 'Subscribe to Use TIDY for Cleaning & Maintenance',
        body: 'TIDY coordinates your cleanings, turnovers, maintenance, inspections & restocking — no markups or per-job fees. Waived for qualifying long-term rentals.',
        confirmText: 'Continue to Subscribe',
        totalLabel: 'Monthly Total'
      };
    }
    return {
      icon: 'auto_awesome',
      title: 'Upgrade to Use TIDY for Cleaning & Maintenance',
      body: 'TIDY coordinates your cleanings, turnovers, maintenance, inspections & restocking — no markups or per-job fees. Waived for qualifying long-term rentals.',
      confirmText: 'Yes, Upgrade My Subscription',
      totalLabel: 'New Monthly Total'
    };
  }

  private buildLineItems(): SubscriptionLineItem[] {
    const propertyManagement: SubscriptionLineItem = {
      icon: 'assets/svg/home-outline.svg',
      title: 'Property Management',
      amount: this.bookingFee
    };
    const cleaningMaintenance: SubscriptionLineItem = {
      icon: 'assets/svg/construct-outline.svg',
      title: 'Cleaning & Maintenance',
      subtitle: `${this.money(this.perUnitDollars)}/unit/mo × ${this.unitCount} ${this.unitCount === 1 ? 'unit' : 'units'}`,
      amount: `${this.cleaningMaintenanceTotal}/mo`
    };

    if (this.mode === 'remove') {
      propertyManagement.subtitle = 'Stays on your plan';
      cleaningMaintenance.subtitle = 'Removed from your plan';
      cleaningMaintenance.struck = true;
      return [propertyManagement, cleaningMaintenance];
    }
    return [propertyManagement, cleaningMaintenance];
  }

  private buildTotal(): string {
    if (this.mode === 'remove') {
      return this.bookingFee;
    }
    return `${this.cleaningMaintenanceTotal}/mo + ${this.bookingFee}`;
  }

  private money(dollars: number): string {
    return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
  }
}
